import React, { useState, useEffect, useCallback } from 'react';
import { teamsApi, usersApi } from '../services/api';
import { Surface, SURFACES, Team, User, USER_ROLES } from '../types';

const roleLabel = (r: string) => r.replace('_', '-');

const EMPTY_USER = { email: '', name: '', phone: '', role: 'advisor', surfaces: ['nurture'] as Surface[], team_id: '', password: '' };

const toggle = (list: Surface[], s: Surface): Surface[] =>
  list.includes(s) ? list.filter((x) => x !== s) : [...list, s];

// One checkbox per portal. Which doors open for this person — orthogonal to
// role (admin-or-not) and to team (whose leads they see).
const SurfaceChecks: React.FC<{ value: Surface[]; onChange: (next: Surface[]) => void }> = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1">
    {SURFACES.map((s) => (
      <label key={s.id} className="inline-flex items-center gap-1.5 text-sm text-charcoal cursor-pointer">
        <input
          type="checkbox"
          checked={value.includes(s.id)}
          onChange={() => onChange(toggle(value, s.id))}
          className="accent-forest"
        />
        {s.label}
      </label>
    ))}
  </div>
);

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const [response, teamRows] = await Promise.all([usersApi.list(), teamsApi.list()]);
      setUsers(response.data);
      setTeams(teamRows.filter((t) => t.is_active));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    if (newUser.password && newUser.password.length < 8) {
      setError('Password must be at least 8 characters (or leave blank for Google-only).');
      setSaving(false);
      return;
    }
    try {
      await usersApi.create({
        email: newUser.email,
        name: newUser.name,
        phone: newUser.phone || undefined,
        role: newUser.role,
        surfaces: newUser.surfaces,
        team_id: newUser.team_id || null,
        password: newUser.password || undefined,
      });
      setNewUser(EMPTY_USER);
      setShowAddForm(false);
      await fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to add user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (user: User) => {
    const pw = window.prompt(
      `Set a new password for ${user.email}.\nMin 8 characters. The user can change it themselves later.`
    );
    if (pw === null) return; // user cancelled
    if (pw.length < 8) {
      window.alert('Password must be at least 8 characters.');
      return;
    }
    try {
      await usersApi.update(user.id, { password: pw });
      window.alert(`Password set for ${user.email}.`);
    } catch (err: any) {
      window.alert(err?.response?.data?.detail || 'Failed to set password');
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await usersApi.update(user.id, { is_active: !user.is_active });
      await fetchUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
    }
  };

  const handleRoleChange = async (user: User, newRole: string) => {
    try {
      await usersApi.update(user.id, { role: newRole });
      await fetchUsers();
    } catch (err) {
      console.error('Failed to update role:', err);
    }
  };

  const handleSurfacesChange = async (user: User, surfaces: Surface[]) => {
    try {
      await usersApi.update(user.id, { surfaces });
      await fetchUsers();
    } catch (err) {
      console.error('Failed to update surfaces:', err);
    }
  };

  // Team = membership, the data-visibility axis (MAG-168). Normally set by
  // the Sell.do import; editable here for a user Sell.do does not know.
  const handleTeamChange = async (user: User, teamId: string) => {
    try {
      await usersApi.update(user.id, { team_id: teamId || null });
      await fetchUsers();
    } catch (err) {
      console.error('Failed to update team:', err);
    }
  };

  const roleOptions = USER_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>);
  const teamOptions = (
    <>
      <option value="">No team</option>
      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </>
  );

  if (loading) {
    return (
      <div className="flex-1 bg-cream flex items-center justify-center">
        <p className="text-graphite">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-cream overflow-auto">
      <div className="w-full px-7 pt-6 pb-16 max-w-5xl">
        {/* Header */}
        <div className="page-head">
          <div>
            <h1 className="page-title">Users</h1>
            <p className="page-sub num">{users.length} people</p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="btn btn-pri"
          >
            {showAddForm ? 'Cancel' : '+ Add User'}
          </button>
        </div>

        {/* Add User Form */}
        {showAddForm && (
          <div className="bg-warm-white border border-sketch rounded-xl p-6 mb-6">
            <h3 className="font-display text-lg font-semibold text-charcoal mb-4">Add New User</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-graphite mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-sketch rounded-lg text-sm focus:outline-none focus:border-forest"
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-graphite mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="w-full px-3 py-2 border border-sketch rounded-lg text-sm focus:outline-none focus:border-forest"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-graphite mb-1">Phone</label>
                  <input
                    type="text"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-sketch rounded-lg text-sm focus:outline-none focus:border-forest"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm text-graphite mb-1">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-sketch rounded-lg text-sm focus:outline-none focus:border-forest bg-warm-white"
                  >
                    {roleOptions}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-graphite mb-1">Team</label>
                  <select
                    value={newUser.team_id}
                    onChange={(e) => setNewUser({ ...newUser, team_id: e.target.value })}
                    className="w-full px-3 py-2 border border-sketch rounded-lg text-sm focus:outline-none focus:border-forest bg-warm-white"
                  >
                    {teamOptions}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-graphite mb-1">
                    Access <span className="text-xs text-graphite/70">(which portals they can sign in to)</span>
                  </label>
                  <SurfaceChecks value={newUser.surfaces} onChange={(surfaces) => setNewUser({ ...newUser, surfaces })} />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-graphite mb-1">
                    Password <span className="text-xs text-graphite/70">(optional — leave blank for Google-only sign-in)</span>
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full px-3 py-2 border border-sketch rounded-lg text-sm focus:outline-none focus:border-forest"
                    placeholder="Min 8 characters"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              {error && (
                <p className="text-brick text-sm">{error}</p>
              )}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-forest text-warm-white rounded-lg hover:bg-forest/90 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {saving ? 'Adding...' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-warm-white border border-sketch rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-sketch bg-cream/50">
                <th className="text-left px-6 py-3 text-xs font-medium text-graphite uppercase tracking-wider">User</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-graphite uppercase tracking-wider">Phone</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-graphite uppercase tracking-wider">Role</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-graphite uppercase tracking-wider">Team</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-graphite uppercase tracking-wider">Access</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-graphite uppercase tracking-wider">Active</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-graphite uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={`border-b border-sketch-soft last:border-0 ${!user.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {user.google_picture_url ? (
                        <img src={user.google_picture_url} alt={user.name || ''} className="w-8 h-8 rounded-full mr-3" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-forest/10 text-forest flex items-center justify-center mr-3 text-sm font-medium">
                          {(user.name || user.email)[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-charcoal">{user.name || '—'}</p>
                        <p className="text-xs text-graphite">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-graphite">
                    {user.phone || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user, e.target.value)}
                      className="px-2 py-1 border border-sketch rounded text-sm focus:outline-none focus:border-forest bg-warm-white"
                    >
                      {roleOptions}
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={user.team_id || ''}
                      onChange={(e) => handleTeamChange(user, e.target.value)}
                      className="px-2 py-1 border border-sketch rounded text-sm focus:outline-none focus:border-forest bg-warm-white max-w-[200px]"
                    >
                      {teamOptions}
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <SurfaceChecks value={user.surfaces} onChange={(surfaces) => handleSurfacesChange(user, surfaces)} />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        user.is_active ? 'bg-forest' : 'bg-charcoal/20'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-warm-white transition-transform ${
                          user.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleResetPassword(user)}
                      className="text-xs text-forest hover:underline"
                      title="Set or reset this user's password"
                    >
                      {user.has_password ? 'Reset password' : 'Set password'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsersPage;
